import { renderLocationLabel } from "./locationScan";

export const LOCATION_LEVELS = ["site", "zone", "aisle", "rackBay", "shelfLevel", "bin"];

export function normalizeLocId(value) {
  if (value == null || value === "") return "";
  if (typeof value === "object" && value.$oid) return String(value.$oid);
  return String(value);
}

export function getPartId(part) {
  if (!part || typeof part !== "object") return "";
  return normalizeLocId(part._id != null ? part._id : part.id);
}

export function isEmptyParent(parentLocation) {
  return parentLocation == null || parentLocation === "" || parentLocation === false;
}

export function getDeepestLevelIndex(location) {
  var deepest = -1;
  for (var i = 0; i < LOCATION_LEVELS.length; i++) {
    var part = location?.[LOCATION_LEVELS[i]];
    if (part && (part.locationName || getPartId(part))) deepest = i;
  }
  return deepest;
}

export function getNodeName(location) {
  if (!location) return "";
  var idx = getDeepestLevelIndex(location);
  if (idx < 0) return renderLocationLabel(location) || "";
  return location[LOCATION_LEVELS[idx]]?.locationName || "";
}

export function trimLocationToLevel(location, levelIndex) {
  var path = {};
  for (var i = 0; i <= levelIndex; i++) {
    var key = LOCATION_LEVELS[i];
    if (location?.[key]) path[key] = location[key];
  }
  return path;
}

export function locationMatchesAncestors(loc, parentPath, throughLevelIndex) {
  for (var i = 0; i <= throughLevelIndex; i++) {
    var key = LOCATION_LEVELS[i];
    if (getPartId(loc?.[key]) !== getPartId(parentPath?.[key])) return false;
  }
  return true;
}

/** Unique root sites (no parentLocation on site). */
export function getRootLocations(locations) {
  var list = Array.isArray(locations) ? locations : [];
  var byId = {};
  list.forEach((loc) => {
    if (!loc?.site) return;
    if (!isEmptyParent(loc.site.parentLocation)) return;
    var id = getPartId(loc.site);
    if (!id) return;
    var siteOnly = getDeepestLevelIndex(loc) === 0;
    if (!byId[id] || siteOnly) {
      byId[id] = siteOnly ? loc : { site: loc.site };
    }
  });
  return Object.values(byId);
}

/** Direct children of a location path (next hierarchy level via parentLocation → parent _id). */
export function getChildLocations(locations, parentPath) {
  var list = Array.isArray(locations) ? locations : [];
  if (!parentPath) return [];
  var parentLevel = getDeepestLevelIndex(parentPath);
  if (parentLevel < 0 || parentLevel >= LOCATION_LEVELS.length - 1) return [];
  var childLevel = parentLevel + 1;
  var childKey = LOCATION_LEVELS[childLevel];
  var parentId = getPartId(parentPath[LOCATION_LEVELS[parentLevel]]);
  if (!parentId) return [];

  var byId = {};
  list.forEach((loc) => {
    var child = loc?.[childKey];
    if (!child) return;
    if (normalizeLocId(child.parentLocation) !== parentId) return;
    if (!locationMatchesAncestors(loc, parentPath, parentLevel)) return;
    var id = getPartId(child);
    if (!id) return;
    var hasDeeper = LOCATION_LEVELS.slice(childLevel + 1).some((k) => !!loc?.[k]);
    var path = trimLocationToLevel(loc, childLevel);
    if (!byId[id] || !hasDeeper) {
      byId[id] = path;
    }
  });
  return Object.values(byId);
}

export function locationMatchesSearch(location, query) {
  if (!query) return true;
  var q = query.toLowerCase();
  var full = (renderLocationLabel(location) || "").toLowerCase();
  if (full.includes(q)) return true;
  return LOCATION_LEVELS.some((key) => {
    var name = location?.[key]?.locationName;
    return name && String(name).toLowerCase().includes(q);
  });
}

export function isSameLocation(a, b) {
  if (!a || !b) return false;
  if (a === b) return true;
  var aLevel = getDeepestLevelIndex(a);
  var bLevel = getDeepestLevelIndex(b);
  if (aLevel !== bLevel) return false;
  return LOCATION_LEVELS.every((key, idx) => {
    if (idx > aLevel) return true;
    return getPartId(a?.[key]) === getPartId(b?.[key]);
  });
}

export function locationKey(location) {
  if (!location) return "";
  if (typeof location === "string") return location;
  var idParts = LOCATION_LEVELS.map((key) => getPartId(location?.[key])).filter(Boolean);
  if (idParts.length) return idParts.join(">");
  return renderLocationLabel(location) || "";
}

export function unwrapLocationValue(value) {
  if (!value) return null;
  if (Array.isArray(value)) {
    var first = value[0];
    if (first && typeof first === "object") return first;
    // Historial often stores "default_location" as a string in the fromLocation cell.
    if (typeof first === "string" && first.trim() && first !== "") return first;
    return null;
  }
  if (typeof value === "object") return value;
  if (typeof value === "string" && value.trim()) return value;
  return null;
}
